/*
 * Scope API client. Mirrors /api/scope/* endpoints in app/routes/scope.py.
 * Auth via global axios interceptor — token already attached.
 */

import axios from 'axios';

import type {
  EngagementAttachment,
  ProblemCard,
  ProblemCreateRequest,
  ProblemPatchRequest,
  ScopeStateResponse,
} from './scope-types';

const API_BASE = process.env.REACT_APP_API_URL ?? '';

function url(path: string): string {
  return `${API_BASE}${path}`;
}


export async function fetchScopeState(engagementId: string): Promise<ScopeStateResponse> {
  const res = await axios.get<ScopeStateResponse>(
    url(`/api/scope/${engagementId}/state`),
  );
  return res.data;
}


// ── Problems dashboard: client-level list + per-problem Orbit state ──

// Orbit's gate verdict, persisted on the problem card. Mirrors
// compute_problem_gate(): the clarity read + the decision that follows.
export interface ProblemGate {
  verdict: 'draft_prd' | 'solution_partner' | 'need_deployment'
    | 'need_urgency' | 'need_budget' | 'tell_me_more' | string;
  reason: string;
  clarity?: {
    clarity_score: number;          // 0–1
    verdict: 'clear' | 'needs_scoping' | 'not_a_problem' | string;
    real_problem?: number;          // 0–1
    clear_threshold?: number;       // 0.7
    one_line?: string;
    source?: 'model' | 'rule' | 'empty' | string;
    dimensions?: Record<string, number>;       // 0–1 each
    inferred_frameworks?: string[];
    inferred_data_kinds?: string[];
    compliance_reason?: string;
  };
  regulation?: { regulated: boolean; heavy: boolean; reasons?: string[]; inferred_frameworks?: string[]; inferred_data_kinds?: string[] };
  deployment?: { known: boolean; missing?: string[] };
  urgency_known?: boolean;
  budget_known?: boolean;
  help_level?: string | null;
}

export interface ClientProblem {
  id: string;
  engagement_id: string | null;
  status: string;                 // DRAFT | SCOPED | PRD_READY | ...
  title: string;
  data: Record<string, unknown>;  // the card's captured fields
  captured: number;
  total: number;
  blocking: number;
  gate: ProblemGate | null;       // Orbit's clarity verdict (null until gated)
  /** Where the card sits with the Solution Partner and the client's decision. */
  review_state?: 'draft' | 'needs_review' | 'ready_for_client' | 'awaiting_client' | 'confirmed' | 'declined' | 'routed';
  request_id?: string | null;     // the request the client decides on
  request_status?: string | null;
  partner_summary?: string | null;
  client_decided_by?: string | null;
  client_decided_at?: string | null;
  created_at: string | null;      // for stable "Problem NN" numbering
  updated_at: string | null;
}

export async function listClientProblems(clientId: string): Promise<ClientProblem[]> {
  const res = await axios.get<{ problems: ClientProblem[] }>(
    url(`/api/scope/client/${clientId}/problems`),
  );
  return res.data.problems || [];
}

// Schedule (or place now) an Orbit voice call to a person to fill ONE section.
export async function scheduleSectionCall(engagementId: string, body: {
  to_number: string;
  section?: string;          // single block (back-compat)
  sections?: string[];       // multi-block (preferred)
  problem_id?: string;       // which problem this call is about
  recipient_name?: string;
  scheduled_at?: string;
}): Promise<{ call_id: string; status: string; problem_id: string | null; sections: string[]; scheduled_at: string | null }> {
  const res = await axios.post(url(`/api/scope/${engagementId}/schedule-call`), body);
  return res.data;
}

// Delete a problem (and its cascade). The Problems-page trash action.
export async function deleteProblem(problemId: string): Promise<{ ok: boolean; deleted: string }> {
  const res = await axios.delete(url(`/api/scope/problems/${problemId}`));
  return res.data;
}
// Per-problem live state + confirm reuse the existing fetchProblemNextQuestion /
// confirmProblemField below (no duplicate — the Problems drawer calls those).


export async function confirmScopeField(
  engagementId: string,
  field: string,
  value: unknown,
  action: 'confirm' | 'correct' | 'skip' = 'confirm',
): Promise<{ ok: boolean }> {
  const res = await axios.post<{ ok: boolean }>(
    url(`/api/scope/${engagementId}/confirm`),
    { field, value, action },
  );
  return res.data;
}


export async function createProblem(
  engagementId: string,
  body: ProblemCreateRequest,
): Promise<ProblemCard> {
  const res = await axios.post<ProblemCard>(
    url(`/api/scope/${engagementId}/problems`),
    body,
  );
  return res.data;
}


// ── Per-problem Inquirer flow ─────────────────────────────────────────
//
// Drives the chat conversation when the user is scoping a single problem
// card. /next-question returns the same NextQuestion shape the L1
// Inquirer returns, so the chat primitives can render it as-is.
// /confirm-field writes a single P.1-P.8 field on the problem_card,
// returns the next question + completion stats.

export interface ProblemNextQuestionResponse {
  next_question: import('./dossier-types').NextQuestion | null;
  completion: { captured: number; total: number; blocking: number };
  status: string;
}

export async function fetchProblemNextQuestion(
  problemId: string,
): Promise<ProblemNextQuestionResponse> {
  const res = await axios.get<ProblemNextQuestionResponse>(
    url(`/api/scope/problems/${problemId}/next-question`),
  );
  return res.data;
}

// The canonical problem-card schema, served by /api/scope/problem-schema so a
// page that renders the whole card doesn't hand-maintain its own field list.
export interface ProblemFieldDef {
  key: string;                       // dotted, e.g. "p.statement"
  label: string;
  kind: 'text' | 'longtext' | 'number' | 'date' | 'select' | 'multiselect' | 'url' | 'chip';
  options?: Array<{ value: string; label: string }> | null;
  placeholder?: string | null;
  why?: string | null;
  critical?: boolean;
  required_for_routing?: boolean;
}

export async function fetchProblemSchema(): Promise<ProblemFieldDef[]> {
  const res = await axios.get<{ fields: ProblemFieldDef[] }>(
    url('/api/scope/problem-schema'),
  );
  return res.data.fields || [];
}

export async function confirmProblemField(
  problemId: string,
  field: string,
  value: unknown,
  action: 'confirm' | 'correct' | 'skip' = 'confirm',
): Promise<ProblemNextQuestionResponse & { ok: boolean }> {
  const res = await axios.post<ProblemNextQuestionResponse & { ok: boolean }>(
    url(`/api/scope/problems/${problemId}/confirm-field`),
    { field, value, action },
  );
  return res.data;
}


// ── Per-workflow sub-cards (W.1-W.13) ─────────────────────────────────
//
// Each problem_card spawns 1-N workflow_subcard rows. Per v2.7 lines 542-
// 558: workflows capture the as-is mechanic engineering will encode.

export interface WorkflowSubcard {
  id: string;
  problem_card_id: string;
  data: Record<string, unknown>;
  field_states: Record<string, { state: string; updated_at?: string }>;
  sort_order: number;
  created_at: string | null;
  completion: { captured: number; total: number; blocking: number };
}

export interface WorkflowListResponse {
  workflows: WorkflowSubcard[];
}

export interface WorkflowNextQuestionResponse {
  next_question: import('./dossier-types').NextQuestion | null;
  completion: { captured: number; total: number; blocking: number };
}

export async function listWorkflows(problemId: string): Promise<WorkflowListResponse> {
  const res = await axios.get<WorkflowListResponse>(
    url(`/api/scope/problems/${problemId}/workflows`),
  );
  return res.data;
}

export async function createWorkflow(
  problemId: string,
  data?: Record<string, unknown>,
): Promise<WorkflowSubcard> {
  const res = await axios.post<WorkflowSubcard>(
    url(`/api/scope/problems/${problemId}/workflows`),
    { data: data ?? {} },
  );
  return res.data;
}

export async function fetchWorkflowNextQuestion(
  workflowId: string,
): Promise<WorkflowNextQuestionResponse> {
  const res = await axios.get<WorkflowNextQuestionResponse>(
    url(`/api/scope/workflows/${workflowId}/next-question`),
  );
  return res.data;
}

export async function confirmWorkflowField(
  workflowId: string,
  field: string,
  value: unknown,
  action: 'confirm' | 'correct' | 'skip' = 'confirm',
): Promise<WorkflowNextQuestionResponse & { ok: boolean }> {
  const res = await axios.post<WorkflowNextQuestionResponse & { ok: boolean }>(
    url(`/api/scope/workflows/${workflowId}/confirm-field`),
    { field, value, action },
  );
  return res.data;
}

export async function deleteWorkflow(workflowId: string): Promise<{ ok: boolean }> {
  const res = await axios.delete<{ ok: boolean }>(
    url(`/api/scope/workflows/${workflowId}`),
  );
  return res.data;
}


// ── Per-problem Match Engine ─────────────────────────────────────────

export interface ProblemOptionCard {
  kind: 'marketplace' | 'template' | 'custom';
  solution_id: string | null;
  solution_slug: string | null;
  name: string;
  tagline: string;
  fit_score: number;
  tool_compat_score: number;
  cust_needed_score: number;
  solves: string[];
  misses: string[];
  price_summary: string;
  timeline_summary: string;
  ip_terms: string;
  compliance: string;
  rationale: string[];
  landing_url: string | null;
}

export interface ProblemOptionsResponse {
  problem_id: string;
  eligible: boolean;
  ineligible_reason: string | null;
  cards: ProblemOptionCard[];
  marketplace_match_count: number;
}

export async function fetchProblemOptions(problemId: string): Promise<ProblemOptionsResponse> {
  const res = await axios.get<ProblemOptionsResponse>(
    url(`/api/scope/problems/${problemId}/options`),
  );
  return res.data;
}

export async function pickProblemOption(
  problemId: string,
  kind: ProblemOptionCard['kind'],
  solutionSlug?: string | null,
): Promise<{ ok: boolean; status: string; pick: Record<string, unknown> }> {
  const res = await axios.post<{ ok: boolean; status: string; pick: Record<string, unknown> }>(
    url(`/api/scope/problems/${problemId}/pick-option`),
    { kind, solution_slug: solutionSlug ?? null },
  );
  return res.data;
}


// ── Experience Report ────────────────────────────────────────────────

export interface ExperienceReportSection1 {
  maturity_stage: string;
  maturity_label: string;
  adoption_posture: string | null;
  prior_investment: string | null;
  company_size: string | null;
  industry: string | null;
  stage_company: string | null;
  readiness_summary: string;
}

export interface ExperienceReportProposal {
  problem_id: string;
  statement: string;
  priority: string | null;
  approach: string | null;
  success_metric: string | null;
  target: string | null;
  deadline: string | null;
  status: string;
}

export interface ExperienceReportScopedAgent {
  problem_id: string;
  problem_statement: string;
  kind: string;
  kind_label: string;
  solution_slug: string | null;
  validx_project_id: string | null;
  prd_drafted: boolean;
  status: string;
}

export interface ExperienceReportActionPlanItem {
  problem_id: string;
  statement: string;
  status: string;
  milestone: string;
}

export interface ExperienceReportForwardPath {
  // 'both' was retired in favor of 2 clear options: build OR talk to a
  // consultant. Legacy engagements with forward_path='both' still load
  // (the backend stores the raw string) but the picker only surfaces
  // build / training.
  id: 'build' | 'training';
  label: string;
  tagline: string;
  detail: string;
}

export interface ExperienceReportSectionK {
  primary_outcome: string;
  baseline: string;
  target: string;
  deadline: string | null;
  counter_metrics: string[];
  auto_drafted: boolean;
}

export interface ExperienceReportSectionP {
  master_metric: string;
  milestone_30: string;
  milestone_60: string;
  milestone_90: string;
  failure_thresholds: string[];
  measurement_owner: string;
  auto_drafted: boolean;
}

export interface ExperienceReportTechniqueItem {
  department: string;
  technique: string;
  headline: string;
  detail: string;
  problem_count: number;
}

export interface ExperienceReportSection3 {
  items: ExperienceReportTechniqueItem[];
  placeholder: boolean;
  note: string;
}

export interface ExperienceReportToolBucket {
  tool: string;
  workflow_count: number;
}

export interface ExperienceReportSection4 {
  leverage_existing: ExperienceReportToolBucket[];
  new_connections: ExperienceReportToolBucket[];
  retire_candidates: Array<{ name: string }>;
  placeholder: boolean;
  note: string;
}

export interface ExperienceReportResponse {
  engagement_id: string;
  ready: boolean;
  problem_count: number;
  scoped_count: number;
  prd_ready_count: number;
  burned_upsell: boolean;
  sections: {
    '1_where_you_are': ExperienceReportSection1;
    '2_proposals': ExperienceReportProposal[];
    '3_recommended_techniques': ExperienceReportSection3;
    '4_recommended_tools': ExperienceReportSection4;
    '5_scoped_agents': ExperienceReportScopedAgent[];
    '6_skill_packs': Array<{ problem_id: string; statement: string; affected_departments: string[] }>;
    '7_action_plan': {
      days_30: ExperienceReportActionPlanItem[];
      days_60: ExperienceReportActionPlanItem[];
      days_90: ExperienceReportActionPlanItem[];
    };
    k_outcomes: ExperienceReportSectionK;
    p_scorecard: ExperienceReportSectionP;
  };
  forward_paths: ExperienceReportForwardPath[];
}

export async function fetchExperienceReport(engagementId: string): Promise<ExperienceReportResponse> {
  const res = await axios.get<ExperienceReportResponse>(
    url(`/api/scope/${engagementId}/experience-report`),
  );
  return res.data;
}

export async function pickForwardPath(
  engagementId: string,
  path: ExperienceReportForwardPath['id'],
): Promise<{ ok: boolean; forward_path: string }> {
  const res = await axios.post<{ ok: boolean; forward_path: string }>(
    url(`/api/scope/${engagementId}/forward-path`),
    { path },
  );
  return res.data;
}


export async function patchProblem(
  problemId: string,
  body: ProblemPatchRequest,
): Promise<{ ok: boolean }> {
  const res = await axios.patch<{ ok: boolean }>(
    url(`/api/scope/problems/${problemId}`),
    body,
  );
  return res.data;
}


// ── Scope share tokens — per-section delegation ──────────────────────
//
// Owner issues a JWT scoped to one section + engagement; recipient hits
// /orbit/delegated/<token_id> and fills only that section. Backend at
// POST /api/scope/{eid}/share.

export interface ScopeShareIssueResponse {
  token_id: string;
  section: string;
  layer: 'L1' | 'L2';
  expires_at: string | null;
  share_url: string;
}

/** Issue a share link for one section.
 *
 *  Addressed by engagement when there is one, and by company when there is
 *  not. A company has a profile long before it has an engagement, so keying
 *  this only on the engagement meant the share button had nothing to call for
 *  most companies and was hidden from them. L2 sections still require an
 *  engagement, because deep scoping belongs to one. */
export async function issueScopeShareToken(body: {
  engagementId?: string | null;
  clientId?: string | null;
  section: string;
  layer: 'L1' | 'L2';
  recipient_email?: string;
  expires_days?: number;
  // "Everything in this phase" — one link covering all the phase's blocks.
  phase?: string;
  // Explicit multi-section scope (e.g. company A/B/C/D) — one link, many blocks.
  sections?: string[];
}): Promise<ScopeShareIssueResponse> {
  const { engagementId, clientId, ...rest } = body;
  if (!engagementId && !clientId) {
    throw new Error('A share link needs either an engagement or a company.');
  }
  const path = engagementId
    ? `/api/scope/${engagementId}/share`
    : `/api/scope/client/${clientId}/share`;
  const res = await axios.post<ScopeShareIssueResponse>(url(path), rest);
  return res.data;
}


// ── Attachments ─────────────────────────────────────────────────────


export async function attachFile(
  engagementId: string,
  file: File,
): Promise<EngagementAttachment> {
  const form = new FormData();
  form.append('file', file);
  const res = await axios.post<EngagementAttachment>(
    url(`/api/scope/${engagementId}/attach-file`),
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data;
}


export async function attachUrl(
  engagementId: string,
  link: string,
): Promise<EngagementAttachment> {
  const res = await axios.post<EngagementAttachment>(
    url(`/api/scope/${engagementId}/attach-url`),
    { url: link },
  );
  return res.data;
}


export async function attachNote(
  engagementId: string,
  note: string,
): Promise<EngagementAttachment> {
  const res = await axios.post<EngagementAttachment>(
    url(`/api/scope/${engagementId}/attach-note`),
    { note },
  );
  return res.data;
}


export async function deleteAttachment(
  engagementId: string,
  attachmentId: string,
): Promise<{ ok: boolean }> {
  const res = await axios.delete<{ ok: boolean }>(
    url(`/api/scope/${engagementId}/attachments/${attachmentId}`),
  );
  return res.data;
}
